import { supabase } from '@/integrations/supabase/client';
import { 
  MaterialityTopic, 
  MaterialityPoint, 
  Stakeholder, 
  IndustryBenchmark, 
  MaterialityReport, 
  MaterialitySettings,
  MaterialityAssessment,
  MaterialityMatrixData,
  StakeholderEngagement,
  MaterialityInsight
} from '@/types/materiality';

class MaterialityService {
  private static instance: MaterialityService;
  private cache: Map<string, any> = new Map();
  private cacheExpiry = 2 * 60 * 1000; // 2 minutes (reduced from 10 minutes)
  private abortController: AbortController | null = null;

  public static getInstance(): MaterialityService {
    if (!MaterialityService.instance) {
      MaterialityService.instance = new MaterialityService();
    }
    return MaterialityService.instance;
  }

  // Materiality Topics
  async getMaterialityTopics(): Promise<MaterialityTopic[]> {
    try {
      const cacheKey = 'materiality_topics';
      const cachedData = this.getCachedData(cacheKey);
      
      if (cachedData) {
        return cachedData;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }

      // Cancel any existing request
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      // Optimized query: Get user data and sample data in parallel
      const [userDataResult, sampleDataResult] = await Promise.all([
        supabase
          .from('materiality_topics')
          .select('*')
          .eq('organization_id', user.id)
          .order('priority', { ascending: false }),
        supabase
          .from('materiality_topics')
          .select('*')
          .is('organization_id', null)
          .order('priority', { ascending: false })
      ]);

      if (userDataResult.error) throw userDataResult.error;
      if (sampleDataResult.error) throw sampleDataResult.error;

      // Use user data if available, otherwise fall back to sample data
      const data = (userDataResult.data && userDataResult.data.length > 0) 
        ? userDataResult.data 
        : sampleDataResult.data;

      // Transform database fields to match interface
      const transformedData = (data || []).map(topic => ({
        id: topic.id,
        name: topic.name,
        category: topic.category,
        businessImpact: topic.business_impact,
        stakeholderImportance: topic.stakeholder_importance,
        priority: topic.priority,
        status: topic.status,
        lastAssessment: topic.last_assessment,
        nextReview: topic.next_review,
        stakeholders: topic.stakeholders_count,
        actions: topic.actions_count,
        description: topic.description,
        quadrant: topic.quadrant
      }));

      this.setCachedData(cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
        return [];
      }
      console.error('Error fetching materiality topics:', error);
      return [];
    }
  }

  async createMaterialityTopic(topic: Omit<MaterialityTopic, 'id'>): Promise<MaterialityTopic | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Transform interface fields to database fields
      const dbTopic = {
        organization_id: user.id,
        name: topic.name,
        category: topic.category,
        business_impact: topic.businessImpact,
        stakeholder_importance: topic.stakeholderImportance,
        priority: topic.priority,
        status: topic.status,
        quadrant: topic.quadrant,
        description: topic.description,
        last_assessment: topic.lastAssessment,
        next_review: topic.nextReview,
        stakeholders_count: topic.stakeholders,
        actions_count: topic.actions
      };

      const { data, error } = await supabase
        .from('materiality_topics')
        .insert([dbTopic])
        .select()
        .single();

      if (error) throw error;

      this.clearCache('materiality_topics');
      
      // Transform back to interface format
      return {
        id: data.id,
        name: data.name,
        category: data.category,
        businessImpact: data.business_impact,
        stakeholderImportance: data.stakeholder_importance,
        priority: data.priority,
        status: data.status,
        lastAssessment: data.last_assessment,
        nextReview: data.next_review,
        stakeholders: data.stakeholders_count,
        actions: data.actions_count,
        description: data.description,
        quadrant: data.quadrant
      };
    } catch (error) {
      console.error('Error creating materiality topic:', error);
      return null;
    }
  }

  async updateMaterialityTopic(id: string, updates: Partial<MaterialityTopic>): Promise<MaterialityTopic | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // First, check if this is sample data (organization_id is NULL)
      const { data: existingTopic, error: fetchError } = await supabase
        .from('materiality_topics')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // If this is sample data (organization_id is NULL), create a new user copy
      if (!existingTopic.organization_id) {
        console.log('Converting sample topic to user data:', id);
        
        // Create new user topic with updated data
        const newUserTopic = {
          organization_id: user.id,
          name: updates.name || existingTopic.name,
          category: updates.category || existingTopic.category,
          business_impact: updates.businessImpact || existingTopic.business_impact,
          stakeholder_importance: updates.stakeholderImportance || existingTopic.stakeholder_importance,
          priority: updates.priority || existingTopic.priority,
          status: updates.status || existingTopic.status,
          quadrant: updates.quadrant || existingTopic.quadrant,
          description: updates.description || existingTopic.description,
          last_assessment: updates.lastAssessment || existingTopic.last_assessment,
          next_review: updates.nextReview || existingTopic.next_review,
          stakeholders_count: updates.stakeholders || existingTopic.stakeholders_count,
          actions_count: updates.actions || existingTopic.actions_count
        };

        const { data: newTopic, error: createError } = await supabase
          .from('materiality_topics')
          .insert([newUserTopic])
          .select()
          .single();

        if (createError) throw createError;

        this.clearCache('materiality_topics');
        
        // Return the new user topic
        return {
          id: newTopic.id,
          name: newTopic.name,
          category: newTopic.category,
          businessImpact: newTopic.business_impact,
          stakeholderImportance: newTopic.stakeholder_importance,
          priority: newTopic.priority,
          status: newTopic.status,
          lastAssessment: newTopic.last_assessment,
          nextReview: newTopic.next_review,
          stakeholders: newTopic.stakeholders_count,
          actions: newTopic.actions_count,
          description: newTopic.description,
          quadrant: newTopic.quadrant
        };
      }

      // If this is already user data, update it normally
      const dbUpdates: any = {};
      if (updates.name) dbUpdates.name = updates.name;
      if (updates.category) dbUpdates.category = updates.category;
      if (updates.businessImpact) dbUpdates.business_impact = updates.businessImpact;
      if (updates.stakeholderImportance) dbUpdates.stakeholder_importance = updates.stakeholderImportance;
      if (updates.priority) dbUpdates.priority = updates.priority;
      if (updates.status) dbUpdates.status = updates.status;
      if (updates.quadrant) dbUpdates.quadrant = updates.quadrant;
      if (updates.description) dbUpdates.description = updates.description;
      if (updates.lastAssessment) dbUpdates.last_assessment = updates.lastAssessment;
      if (updates.nextReview) dbUpdates.next_review = updates.nextReview;
      if (updates.stakeholders) dbUpdates.stakeholders_count = updates.stakeholders;
      if (updates.actions) dbUpdates.actions_count = updates.actions;

      const { data, error } = await supabase
        .from('materiality_topics')
        .update(dbUpdates)
        .eq('id', id)
        .eq('organization_id', user.id)
        .select()
        .single();

      if (error) throw error;

      this.clearCache('materiality_topics');
      
      // Transform back to interface format
      return {
        id: data.id,
        name: data.name,
        category: data.category,
        businessImpact: data.business_impact,
        stakeholderImportance: data.stakeholder_importance,
        priority: data.priority,
        status: data.status,
        lastAssessment: data.last_assessment,
        nextReview: data.next_review,
        stakeholders: data.stakeholders_count,
        actions: data.actions_count,
        description: data.description,
        quadrant: data.quadrant
      };
    } catch (error) {
      console.error('Error updating materiality topic:', error);
      return null;
    }
  }

  async deleteMaterialityTopic(id: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // First, check if this is sample data (organization_id is NULL)
      const { data: existingTopic, error: fetchError } = await supabase
        .from('materiality_topics')
        .select('organization_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Prevent deletion of sample data
      if (!existingTopic.organization_id) {
        console.log('Cannot delete sample data:', id);
        throw new Error('Cannot delete sample data. Please copy it to your organization first if you want to modify it.');
      }

      // Only delete user data
      const { error } = await supabase
        .from('materiality_topics')
        .delete()
        .eq('id', id)
        .eq('organization_id', user.id);

      if (error) throw error;

      this.clearCache('materiality_topics');
      return true;
    } catch (error) {
      console.error('Error deleting materiality topic:', error);
      return false;
    }
  }

  // Stakeholders
  async getStakeholders(): Promise<Stakeholder[]> {
    try {
      const cacheKey = 'stakeholders';
      const cachedData = this.getCachedData(cacheKey);
      
      if (cachedData) {
        return cachedData;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }

      // Optimized query: Get user data and sample data in parallel
      const [userDataResult, sampleDataResult] = await Promise.all([
        supabase
          .from('stakeholders')
          .select('*')
          .eq('organization_id', user.id)
          .order('priority', { ascending: false }),
        supabase
          .from('stakeholders')
          .select('*')
          .is('organization_id', null)
          .order('priority', { ascending: false })
      ]);

      if (userDataResult.error) throw userDataResult.error;
      if (sampleDataResult.error) throw sampleDataResult.error;

      // Use user data if available, otherwise fall back to sample data
      const data = (userDataResult.data && userDataResult.data.length > 0) 
        ? userDataResult.data 
        : sampleDataResult.data;

      // Transform database fields to match interface
      const transformedData = (data || []).map(stakeholder => ({
        id: stakeholder.id,
        name: stakeholder.name,
        type: stakeholder.type,
        category: stakeholder.category,
        influence: stakeholder.influence,
        interest: stakeholder.interest,
        engagementLevel: stakeholder.engagement_level,
        contactPerson: stakeholder.contact_person,
        email: stakeholder.email,
        phone: stakeholder.phone,
        organizationName: stakeholder.organization_name,
        lastContact: stakeholder.last_contact,
        nextContact: stakeholder.next_contact,
        notes: stakeholder.notes,
        priority: stakeholder.priority
      }));

      this.setCachedData(cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error fetching stakeholders:', error);
      return [];
    }
  }

  async createStakeholder(stakeholder: Omit<Stakeholder, 'id'>): Promise<Stakeholder | null> {
    try {
      const { data, error } = await supabase
        .from('stakeholders')
        .insert([stakeholder])
        .select()
        .single();

      if (error) throw error;

      this.clearCache('stakeholders');
      return data;
    } catch (error) {
      console.error('Error creating stakeholder:', error);
      return null;
    }
  }

  async updateStakeholder(id: string, updates: Partial<Stakeholder>): Promise<Stakeholder | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // First, check if this is sample data (organization_id is NULL)
      const { data: existingStakeholder, error: fetchError } = await supabase
        .from('stakeholders')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // If this is sample data (organization_id is NULL), create a new user copy
      if (!existingStakeholder.organization_id) {
        console.log('Converting sample stakeholder to user data:', id);
        
        // Create new user stakeholder with updated data
        const newUserStakeholder = {
          organization_id: user.id,
          name: updates.name || existingStakeholder.name,
          type: updates.type || existingStakeholder.type,
          category: updates.category || existingStakeholder.category,
          influence: updates.influence || existingStakeholder.influence,
          interest: updates.interest || existingStakeholder.interest,
          engagement_level: updates.engagementLevel || existingStakeholder.engagement_level,
          contact_person: updates.contactPerson || existingStakeholder.contact_person,
          email: updates.email || existingStakeholder.email,
          phone: updates.phone || existingStakeholder.phone,
          organization_name: updates.organization || existingStakeholder.organization_name,
          last_contact: updates.lastContact || existingStakeholder.last_contact,
          next_contact: updates.nextContact || existingStakeholder.next_contact,
          notes: updates.notes || existingStakeholder.notes,
          priority: updates.priority || existingStakeholder.priority
        };

        const { data: newStakeholder, error: createError } = await supabase
          .from('stakeholders')
          .insert([newUserStakeholder])
          .select()
          .single();

        if (createError) throw createError;

        this.clearCache('stakeholders');
        
        // Return the new user stakeholder
        return {
          id: newStakeholder.id,
          name: newStakeholder.name,
          type: newStakeholder.type,
          category: newStakeholder.category,
          influence: newStakeholder.influence,
          interest: newStakeholder.interest,
          engagementLevel: newStakeholder.engagement_level,
          contactPerson: newStakeholder.contact_person,
          email: newStakeholder.email,
          phone: newStakeholder.phone,
          organization: newStakeholder.organization_name,
          lastContact: newStakeholder.last_contact,
          nextContact: newStakeholder.next_contact,
          notes: newStakeholder.notes,
          materialTopics: [],
          priority: newStakeholder.priority
        };
      }

      // If this is already user data, update it normally
      const dbUpdates: any = {};
      if (updates.name) dbUpdates.name = updates.name;
      if (updates.type) dbUpdates.type = updates.type;
      if (updates.category) dbUpdates.category = updates.category;
      if (updates.influence) dbUpdates.influence = updates.influence;
      if (updates.interest) dbUpdates.interest = updates.interest;
      if (updates.engagementLevel) dbUpdates.engagement_level = updates.engagementLevel;
      if (updates.contactPerson) dbUpdates.contact_person = updates.contactPerson;
      if (updates.email) dbUpdates.email = updates.email;
      if (updates.phone) dbUpdates.phone = updates.phone;
      if (updates.organization) dbUpdates.organization_name = updates.organization;
      if (updates.lastContact) dbUpdates.last_contact = updates.lastContact;
      if (updates.nextContact) dbUpdates.next_contact = updates.nextContact;
      if (updates.notes) dbUpdates.notes = updates.notes;
      if (updates.priority) dbUpdates.priority = updates.priority;

      const { data, error } = await supabase
        .from('stakeholders')
        .update(dbUpdates)
        .eq('id', id)
        .eq('organization_id', user.id)
        .select()
        .single();

      if (error) throw error;

      this.clearCache('stakeholders');
      
      // Transform back to interface format
      return {
        id: data.id,
        name: data.name,
        type: data.type,
        category: data.category,
        influence: data.influence,
        interest: data.interest,
        engagementLevel: data.engagement_level,
        contactPerson: data.contact_person,
        email: data.email,
        phone: data.phone,
        organization: data.organization_name,
        lastContact: data.last_contact,
        nextContact: data.next_contact,
        notes: data.notes,
        materialTopics: [],
        priority: data.priority
      };
    } catch (error) {
      console.error('Error updating stakeholder:', error);
      return null;
    }
  }

  async deleteStakeholder(id: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // First, check if this is sample data (organization_id is NULL)
      const { data: existingStakeholder, error: fetchError } = await supabase
        .from('stakeholders')
        .select('organization_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Prevent deletion of sample data
      if (!existingStakeholder.organization_id) {
        console.log('Cannot delete sample stakeholder:', id);
        throw new Error('Cannot delete sample data. Please copy it to your organization first if you want to modify it.');
      }

      // Only delete user data
      const { error } = await supabase
        .from('stakeholders')
        .delete()
        .eq('id', id)
        .eq('organization_id', user.id);

      if (error) throw error;

      this.clearCache('stakeholders');
      return true;
    } catch (error) {
      console.error('Error deleting stakeholder:', error);
      return false;
    }
  }

  // Industry Benchmarks
  async getIndustryBenchmarks(): Promise<IndustryBenchmark[]> {
    const cacheKey = 'industry_benchmarks';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      // Industry benchmarks are public data, no organization_id needed
      const { data, error } = await supabase
        .from('industry_benchmarks')
        .select(`
          *,
          benchmark_topics (*)
        `)
        .order('industry', { ascending: true });

      if (error) throw error;

      // Transform database fields to match interface
      const transformedData = (data || []).map(benchmark => ({
        id: benchmark.id,
        industry: benchmark.industry,
        sector: benchmark.sector,
        region: benchmark.region,
        year: benchmark.year,
        averageBusinessImpact: benchmark.average_business_impact,
        averageStakeholderImportance: benchmark.average_stakeholder_importance,
        totalTopics: benchmark.total_topics,
        highPriorityTopics: benchmark.high_priority_topics,
        topics: (benchmark.benchmark_topics || []).map((topic: any) => ({
          name: topic.name,
          businessImpact: topic.business_impact,
          stakeholderImportance: topic.stakeholder_importance,
          priority: topic.priority,
          frequency: topic.frequency
        }))
      }));

      this.setCachedData(cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error fetching industry benchmarks:', error);
      return [];
    }
  }

  async getBenchmarksByIndustry(industry: string): Promise<IndustryBenchmark[]> {
    try {
      const { data, error } = await supabase
        .from('industry_benchmarks')
        .select('*')
        .eq('industry', industry)
        .order('year', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching industry benchmarks:', error);
      return [];
    }
  }

  // Materiality Reports
  async getMaterialityReports(): Promise<MaterialityReport[]> {
    const cacheKey = 'materiality_reports';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // First try to get user's own data
      let { data, error } = await supabase
        .from('materiality_reports')
        .select('*')
        .eq('organization_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If no user data exists, get sample data (where organization_id is NULL)
      if (!data || data.length === 0) {
        console.log('No user reports found, fetching sample data...');
        const { data: sampleData, error: sampleError } = await supabase
          .from('materiality_reports')
          .select('*')
          .is('organization_id', null)
          .order('created_at', { ascending: false });

        if (sampleError) throw sampleError;
        data = sampleData;
      }

      // Transform database fields to match interface
      const transformedData = (data || []).map(report => ({
        id: report.id,
        title: report.title,
        type: report.type,
        status: report.status,
        author: report.author,
        createdAt: report.created_at,
        updatedAt: report.updated_at,
        publishedAt: report.published_at,
        description: report.description,
        format: report.format,
        size: report.file_size,
        stakeholders: report.stakeholders_count,
        topics: report.topics_count,
        executiveSummary: report.executive_summary,
        keyFindings: report.key_findings || [],
        recommendations: report.recommendations || []
      }));

      this.setCachedData(cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error fetching materiality reports:', error);
      return [];
    }
  }

  async createMaterialityReport(report: Omit<MaterialityReport, 'id'>): Promise<MaterialityReport | null> {
    try {
      const { data, error } = await supabase
        .from('materiality_reports')
        .insert([report])
        .select()
        .single();

      if (error) throw error;

      this.clearCache('materiality_reports');
      return data;
    } catch (error) {
      console.error('Error creating materiality report:', error);
      return null;
    }
  }

  async updateMaterialityReport(id: string, updates: Partial<MaterialityReport>): Promise<MaterialityReport | null> {
    try {
      const { data, error } = await supabase
        .from('materiality_reports')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      this.clearCache('materiality_reports');
      return data;
    } catch (error) {
      console.error('Error updating materiality report:', error);
      return null;
    }
  }

  // Materiality Settings
  async getMaterialitySettings(): Promise<MaterialitySettings | null> {
    const cacheKey = 'materiality_settings';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // First try to get user's own settings
      let { data, error } = await supabase
        .from('materiality_settings')
        .select('*')
        .eq('organization_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned

      // If no user settings exist, get sample settings (where organization_id is NULL)
      if (!data) {
        console.log('No user settings found, fetching sample settings...');
        const { data: sampleData, error: sampleError } = await supabase
          .from('materiality_settings')
          .select('*')
          .is('organization_id', null)
          .single();

        if (sampleError) throw sampleError;
        data = sampleData;
      }

      if (!data) return null;

      // Transform database fields to match interface
      const transformedData = {
        id: data.id,
        organization: data.organization_name,
        industry: data.industry,
        sector: data.sector,
        region: data.region,
        assessmentFrequency: data.assessment_frequency,
        stakeholderEngagementMethod: data.stakeholder_engagement_method,
        materialityThreshold: data.materiality_threshold,
        maxMaterialTopics: data.max_material_topics,
        enableIndustryBenchmarking: data.enable_industry_benchmarking,
        enableStakeholderMapping: data.enable_stakeholder_mapping,
        enableAutomatedReporting: data.enable_automated_reporting,
        enableNotifications: data.enable_notifications,
        defaultLanguage: data.default_language,
        timezone: data.timezone,
        currency: data.currency,
        customTopics: data.custom_topics || [],
        excludedTopics: data.excluded_topics || [],
        stakeholderCategories: data.stakeholder_categories || [],
        reportingStandards: data.reporting_standards || [],
        lastUpdated: data.updated_at
      };

      this.setCachedData(cacheKey, transformedData);
      return transformedData;
    } catch (error) {
      console.error('Error fetching materiality settings:', error);
      return null;
    }
  }

  async updateMaterialitySettings(updates: Partial<MaterialitySettings>): Promise<MaterialitySettings | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('materiality_settings')
        .update(updates)
        .eq('organization_id', user.id)
        .select()
        .single();

      if (error) throw error;

      this.clearCache('materiality_settings');
      return data;
    } catch (error) {
      console.error('Error updating materiality settings:', error);
      return null;
    }
  }

  // Materiality Matrix
  async getMaterialityMatrix(): Promise<MaterialityMatrixData | null> {
    const cacheKey = 'materiality_matrix';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    try {
      const topics = await this.getMaterialityTopics();
      
      const matrixData: MaterialityMatrixData = {
        topics: topics.map(topic => ({
          id: topic.id,
          name: topic.name,
          category: topic.category,
          businessImpact: topic.businessImpact,
          stakeholderImportance: topic.stakeholderImportance,
          priority: topic.priority,
          quadrant: topic.quadrant,
          description: topic.description || '',
          stakeholders: [],
          actions: []
        })),
        quadrants: {
          'high-high': topics.filter(t => t.quadrant === 'high-high').map(t => ({
            id: t.id,
            name: t.name,
            category: t.category,
            businessImpact: t.businessImpact,
            stakeholderImportance: t.stakeholderImportance,
            priority: t.priority,
            quadrant: t.quadrant,
            description: t.description || '',
            stakeholders: [],
            actions: []
          })),
          'high-low': topics.filter(t => t.quadrant === 'high-low').map(t => ({
            id: t.id,
            name: t.name,
            category: t.category,
            businessImpact: t.businessImpact,
            stakeholderImportance: t.stakeholderImportance,
            priority: t.priority,
            quadrant: t.quadrant,
            description: t.description || '',
            stakeholders: [],
            actions: []
          })),
          'low-high': topics.filter(t => t.quadrant === 'low-high').map(t => ({
            id: t.id,
            name: t.name,
            category: t.category,
            businessImpact: t.businessImpact,
            stakeholderImportance: t.stakeholderImportance,
            priority: t.priority,
            quadrant: t.quadrant,
            description: t.description || '',
            stakeholders: [],
            actions: []
          })),
          'low-low': topics.filter(t => t.quadrant === 'low-low').map(t => ({
            id: t.id,
            name: t.name,
            category: t.category,
            businessImpact: t.businessImpact,
            stakeholderImportance: t.stakeholderImportance,
            priority: t.priority,
            quadrant: t.quadrant,
            description: t.description || '',
            stakeholders: [],
            actions: []
          }))
        },
        summary: {
          totalTopics: topics.length,
          highPriority: topics.filter(t => t.priority === 'high').length,
          mediumPriority: topics.filter(t => t.priority === 'medium').length,
          lowPriority: topics.filter(t => t.priority === 'low').length,
          environmental: topics.filter(t => t.category === 'environmental').length,
          social: topics.filter(t => t.category === 'social').length,
          governance: topics.filter(t => t.category === 'governance').length
        }
      };

      this.setCachedData(cacheKey, matrixData);
      return matrixData;
    } catch (error) {
      console.error('Error generating materiality matrix:', error);
      return null;
    }
  }

  // Stakeholder Engagement
  async getStakeholderEngagements(): Promise<StakeholderEngagement[]> {
    try {
      const { data, error } = await supabase
        .from('stakeholder_engagements')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching stakeholder engagements:', error);
      return [];
    }
  }

  async createStakeholderEngagement(engagement: Omit<StakeholderEngagement, 'id'>): Promise<StakeholderEngagement | null> {
    try {
      const { data, error } = await supabase
        .from('stakeholder_engagements')
        .insert([engagement])
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating stakeholder engagement:', error);
      return null;
    }
  }

  // Materiality Insights
  async getMaterialityInsights(): Promise<MaterialityInsight[]> {
    try {
      const { data, error } = await supabase
        .from('materiality_insights')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching materiality insights:', error);
      return [];
    }
  }

  async createMaterialityInsight(insight: Omit<MaterialityInsight, 'id'>): Promise<MaterialityInsight | null> {
    try {
      const { data, error } = await supabase
        .from('materiality_insights')
        .insert([insight])
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating materiality insight:', error);
      return null;
    }
  }

  // Cache Management
  private getCachedData(key: string): any {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  private setCachedData(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  public clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Utility Methods
  calculateQuadrant(businessImpact: number, stakeholderImportance: number): 'high-high' | 'high-low' | 'low-high' | 'low-low' {
    if (businessImpact >= 4 && stakeholderImportance >= 4) return 'high-high';
    if (businessImpact >= 4 && stakeholderImportance < 4) return 'high-low';
    if (businessImpact < 4 && stakeholderImportance >= 4) return 'low-high';
    return 'low-low';
  }

  calculatePriority(businessImpact: number, stakeholderImportance: number): 'high' | 'medium' | 'low' {
    const average = (businessImpact + stakeholderImportance) / 2;
    if (average >= 4) return 'high';
    if (average >= 2.5) return 'medium';
    return 'low';
  }
}

export const materialityService = MaterialityService.getInstance(); 